/**
 * Top Products by Savings section: donut chart (Top SKUs) + table.
 * Data: first page from savings-insights/products (ordered by -total_saved).
 * Interactions (v1): alert-based placeholders for drill-in and details sidebar.
 */
import { Box } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toNumber } from 'shared/lib/number/number';
import { getPropertiesDisplayLabel } from 'shared/lib/reports';
import { ReportPropertiesTooltip } from 'shared/ui/ReportPropertiesTooltip';
import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
    augmentItemsForTopNOther,
    buildTopNChartItemsWithOther,
} from 'widgets/ComposableReportWidget';
import {
    buildSavingsRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

import type {
    ProductMetricsDto,
    ProductsResponseDto,
} from 'entities/ReportSavingsSummary';

const renderPropertiesTooltipForProducts = (item: ReportWidgetItem) => (
    <ReportPropertiesTooltip dto={item.meta?.productMetrics} />
);

type SavingsSummaryProductsTableColumnId =
    | 'item'
    | 'brand'
    | 'sku'
    | 'properties'
    | 'quantity'
    | 'avgSupplierPrice'
    | 'pricePaid'
    | 'totalSaved'
    | 'percentSaved';

function productToWidgetItem(
    dto: ProductMetricsDto,
    propertiesDisplay: string,
): ReportWidgetItem {
    const totalPaidNum =
        Number.parseFloat(dto.totalPaid as unknown as string) || 0;
    const totalSavedNum =
        Number.parseFloat(dto.totalSaved as unknown as string) || 0;
    const avgSupplierTotal = totalPaidNum + totalSavedNum;

    return {
        id: `${dto.itemSku}-${dto.propertyCount}-${dto.totalQty}`,
        label: dto.itemName,
        primaryValue: totalSavedNum,
        secondaryValues: {
            itemName: dto.itemName,
            brand: dto.brand ?? '',
            sku: dto.itemSku,
            propertiesDisplay,
            quantity: dto.totalQty,
            avgSupplierPriceTotal: avgSupplierTotal,
            pricePaidTotal: totalPaidNum,
            totalSavedTotal: totalSavedNum,
            percentSavedTotal: Number.parseFloat(dto.percentSaved) || 0,
        },
        meta: { productMetrics: dto },
    };
}

interface SavingsSummaryProductsSectionProps {
    products: ProductsResponseDto | null;
    totalSavingsAmount?: string;
    loading?: boolean;
    onProductDetailsClick?: (sku: string) => void;
    onOpenProductDrillIn?: (
        sku: string,
        product: ProductMetricsDto,
        fromMissedSavings?: boolean,
    ) => void;
    disableChartAnimation?: boolean;
    filtersKey?: string;
    tableOnly?: boolean;
    subheader?: string;
    showBottomBorder?: boolean;
    tableColumnIds?: SavingsSummaryProductsTableColumnId[];
}

export const SavingsSummaryProductsSection = memo(
    ({
        products,
        totalSavingsAmount,
        loading = false,
        onProductDetailsClick,
        onOpenProductDrillIn,
        disableChartAnimation = false,
        filtersKey,
        tableOnly = false,
        subheader,
        showBottomBorder = true,
        tableColumnIds,
    }: SavingsSummaryProductsSectionProps) => {
        const { t } = useTranslation();

        const items: ReportWidgetItem[] = useMemo(() => {
            if (!products) return [];

            const sorted = [...products.items]
                .filter((dto) => (Number.parseFloat(dto.totalSaved) || 0) > 0)
                .sort((a, b) => {
                    const aSaved = Number.parseFloat(a.totalSaved) || 0;
                    const bSaved = Number.parseFloat(b.totalSaved) || 0;
                    return bSaved - aSaved;
                });

            const baseItems = sorted.map((dto) =>
                productToWidgetItem(dto, getPropertiesDisplayLabel(dto, t)),
            );

            return augmentItemsForTopNOther({
                items: baseItems,
                topCount: 5,
                otherChartItemId: 'savings-summary-products-other',
            });
        }, [products, t]);

        const chartItems: ReportWidgetItem[] = useMemo(() => {
            if (tableOnly) {
                return [];
            }

            return buildTopNChartItemsWithOther({
                items,
                totalAmount: totalSavingsAmount,
                topCount: 5,
                otherItemId: 'savings-summary-products-other',
                otherLabel: t('Other'),
            });
        }, [items, totalSavingsAmount, t, tableOnly]);

        const handleDetailsInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                const meta = payload.item.meta;
                const dto = meta.productMetrics;
                const sku = dto?.itemSku;

                if (sku && onProductDetailsClick) {
                    onProductDetailsClick(sku);
                }
            },
            [onProductDetailsClick],
        );

        const handleRowInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                const meta = payload.item.meta;
                const dto = meta.productMetrics;
                const sku = dto?.itemSku;

                if (sku && onOpenProductDrillIn) {
                    onOpenProductDrillIn(sku, dto);
                }
            },
            [onOpenProductDrillIn],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<
                SavingsSummaryProductsTableColumnId,
                ReportWidgetColumnConfig
            > = {
                item: {
                    id: 'item',
                    label: t('Item'),
                    valueKey: 'itemName',
                    format: 'text',
                    align: 'left',
                    bold: true,
                    isDetailsTrigger: true,
                    minWidth: 200,
                    allowWrap: true,
                },
                brand: {
                    id: 'brand',
                    label: t('Brand'),
                    valueKey: 'brand',
                    format: 'text',
                    align: 'left',
                    minWidth: 160,
                    maxWidth: 260,
                    allowWrap: true,
                },
                sku: {
                    id: 'sku',
                    label: t('SKU'),
                    valueKey: 'sku',
                    format: 'text',
                    align: 'left',
                },
                properties: {
                    id: 'properties',
                    label: t('Properties'),
                    valueKey: 'propertiesDisplay',
                    format: 'text',
                    align: 'left',
                    minWidth: 120,
                    maxWidth: 150,
                    allowWrap: true,
                    getCellTooltip: renderPropertiesTooltipForProducts,
                },
                quantity: {
                    id: 'quantity',
                    label: t('Quantity'),
                    valueKey: 'quantity',
                    format: 'number',
                    align: 'right',
                },
                avgSupplierPrice: {
                    id: 'avgSupplierPrice',
                    label: t('savingsSummary.productsColumnAvgSupplierPrice'),
                    valueKey: 'avgSupplierPriceTotal',
                    format: 'currency',
                    align: 'right',
                },
                pricePaid: {
                    id: 'pricePaid',
                    label: t('savingsSummary.productsColumnPricePaid'),
                    valueKey: 'pricePaidTotal',
                    format: 'currency',
                    align: 'right',
                },
                totalSaved: {
                    id: 'totalSaved',
                    label: t('savingsSummary.totalSavings'),
                    valueKey: 'totalSavedTotal',
                    format: 'currency',
                    align: 'right',
                    highlightColor: 'metrics.positive',
                },
                percentSaved: {
                    id: 'percentSaved',
                    label: t('savingsSummary.productsColumnPercentSaved'),
                    valueKey: 'percentSavedTotal',
                    format: 'percent',
                    align: 'right',
                    highlightColor: 'metrics.positive',
                },
            };

            const defaultOrder: SavingsSummaryProductsTableColumnId[] = [
                'item',
                'brand',
                'sku',
                'properties',
                'quantity',
                'avgSupplierPrice',
                'pricePaid',
                'totalSaved',
                'percentSaved',
            ];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            if (disableChartAnimation) {
                const idx = orderedIds.indexOf('properties');
                if (idx !== -1) {
                    orderedIds.splice(idx, 1);
                }
            }

            return orderedIds
                .map((id) => allColumnsById[id])
                .filter((column): column is ReportWidgetColumnConfig =>
                    Boolean(column),
                );
        }, [t, tableColumnIds, disableChartAnimation]);

        const config: ReportWidgetConfig = useMemo(() => {
            const tooltipRowsBuilder: ReportWidgetTooltipRowsBuilder = (
                args,
            ) => {
                if (args.aggregation?.kind === 'other') {
                    const entry = args.payload?.[0];
                    const aggregatedValue = toNumber(
                        entry?.value as number | string | null,
                    );

                    return [
                        ...args.aggregation.headerRows,
                        {
                            ...buildSavingsRow({
                                id: 'savings',
                                label: t('Savings'),
                                amount: aggregatedValue,
                                total: args.total,
                                valueFormatter: args.valueFormatter,
                            }),
                        },
                    ];
                }

                const resolved = getTooltipPointFromPayload(args);
                const item = resolved?.item;

                if (!item) {
                    return null;
                }

                const sv = item.secondaryValues ?? {};
                const avgSupplierTotal = toNumber(sv.avgSupplierPriceTotal);
                const pricePaidTotal = toNumber(sv.pricePaidTotal);
                const totalSaved = toNumber(sv.totalSavedTotal);

                const rows: ReportWidgetTooltipRowConfig[] = [
                    {
                        id: 'header',
                        variant: 'header',
                        label: item.label,
                        boldLabel: true,
                        mutedLabel: true,
                        marginBottom: 8,
                    },
                    {
                        id: 'avgSupplier',
                        variant: 'labelValue',
                        label: t(
                            'savingsSummary.productsColumnAvgSupplierPrice',
                        ),
                        value: args.valueFormatter(avgSupplierTotal),
                    },
                    {
                        id: 'pricePaid',
                        variant: 'labelValue',
                        label: t('savingsSummary.productsColumnPricePaid'),
                        value: args.valueFormatter(pricePaidTotal),
                    },
                    {
                        id: 'divider',
                        variant: 'divider',
                    },
                    {
                        ...buildSavingsRow({
                            id: 'savings',
                            label: t('Savings'),
                            amount: totalSaved,
                            total: args.total,
                            valueFormatter: args.valueFormatter,
                        }),
                    },
                ];

                return rows;
            };

            return {
                widgetId: 'savings-summary-products',
                title: t('savingsSummary.productsSectionTitle'),
                description: subheader,
                modelLabel: t('savingsSummary.modelLabelItems'),
                layout: tableOnly ? 'stacked' : 'sideBySide',
                visualization: {
                    enabled: !tableOnly,
                    type: 'donut',
                    options: {
                        palette: 'positive',
                        showLegend: false,
                        valueFormatter: (v) =>
                            new Intl.NumberFormat(undefined, {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(
                                typeof v === 'number'
                                    ? v
                                    : Number.parseFloat(String(v)) || 0,
                            ),
                        tooltipRowsBuilder,
                    },
                },
                pagination: {
                    mode: 'client',
                    pageSize: 5,
                    revealAllOnLoadMore: true,
                },
                table: {
                    enabled: true,
                    columns: tableColumns,
                    options: {
                        showColorSwatchInLabelColumn: tableOnly
                            ? undefined
                            : 'itemName',
                        loadMoreVariant: 'link',
                    },
                },
                onRowClick: handleRowInteraction,
                onChartItemClick: tableOnly ? undefined : handleRowInteraction,
                onDetailsClick: handleDetailsInteraction,
            };
        }, [
            t,
            handleRowInteraction,
            handleDetailsInteraction,
            tableOnly,
            subheader,
            tableColumns,
        ]);

        return (
            <Box
                data-testclass="savings-summary-products-section"
                data-pdf-section="true"
                data-pdf-force-page-break="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    key={filtersKey}
                    config={config}
                    items={items}
                    chartItems={chartItems}
                    loading={loading}
                    isPrinting={disableChartAnimation}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SavingsSummaryProductsSection.displayName = 'SavingsSummaryProductsSection';
